import { useCallback, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import { Form, FormInstance } from "@/atoms"
import { EXTERNAL_ROUTES, VALUES_PARAMS } from "@/const/routes.constants"
import { buildCreateIssuePayload } from "@/helpers/jira.helpers"
import { InformationAlert } from "@/molecules/InformationAlert"
import { CreateTicketButton } from "@/organisms/CreateTicketButton"
import { JiraTemplateModal } from "@/organisms/Ticketing/components/JiraTemplateModal"
import { useTicketing } from "@/organisms/Ticketing/hooks/useTicketing"
import {
  useActionsWorkflow,
  useTemplateActionTest
} from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import { transformWorkflowPayloadToFormValues } from "./helpers"

import styles from "../ActionsSectionForm.module.scss"

import { useUser } from "@/hooks/useUser"
import { ReadOutlined } from "@/icons"
import { LinkButton } from "@/molecules/LinkButton"
import type {
  JiraTemplateFormValues,
  TestActionResult
} from "@/organisms/Ticketing/components/JiraTemplateModal/JiraTemplateModal.types"
import {
  getTemplateButtonT,
  getWritePermission
} from "@/pages/GovernancePage/helpers/workflows"
import type { ActionProps } from ".."

export interface JiraIssueActionProps extends ActionProps {
  form: FormInstance
  params: { trigger_type?: string }
}

const BUTTON_PREFIX =
  "settings.automatedActions.form.fields.selectActionPlaceholder"

export const JiraIssueAction = (props: JiraIssueActionProps) => {
  const { path, form, t, params } = props
  const { trigger_type: triggerType } = params
  const actions = Form.useWatch("actions", form)
  const jiraFieldsMetadataFieldName = "jiraFieldsMetadata"
  const fieldPath = ["actions", path, "jiraIssue"]
  const fieldsMetadataPath = ["actions", path, jiraFieldsMetadataFieldName]
  // No field renders the error, so its value has to be preserved to be watched
  const relatedError = Form.useWatch<Jira.IssueError | undefined>(
    ["actions", path, "relatedError"],
    { form, preserve: true }
  )
  const jiraIssueTemplate = Form.useWatch(fieldPath, form)
  const { systems } = useTicketing()
  const hasIntegration = systems.includes("jira")
  const [modalType, setModalType] = useState<"jira" | undefined>(undefined)

  const { response: extra, isLoading } = useActionsWorkflow(params, {
    enabled: !!params
  })
  const [
    { testTemplate },
    { isPending: isTestLoading, isSuccess: isTestSuccess }
  ] = useTemplateActionTest()
  const { id = VALUES_PARAMS.NEW_ENTITY } = useParams()
  const { hasPermission } = useUser()
  const userHasWritePermission = hasPermission(getWritePermission()) || false

  // Transform the template from workflow format to form values format
  const transformedTemplate = useMemo(() => {
    if (!jiraIssueTemplate) return undefined
    const { onDemandFields, fields } = jiraIssueTemplate
    form.setFieldValue([...fieldPath, "fields"], fields)
    form.setFieldValue([...fieldPath, "onDemandFields"], onDemandFields)

    return transformWorkflowPayloadToFormValues(jiraIssueTemplate)
  }, [jiraIssueTemplate])

  // Create test handler that wraps the API call - same signature as handleCloseModal
  const handleTest = useCallback(
    async (
      jiraIssueTemplateValues?: JiraTemplateFormValues,
      fieldsMetadata?: Record<string, Jira.IssueTypeField>
    ): Promise<TestActionResult> => {
      if (!jiraIssueTemplateValues || !fieldsMetadata) {
        return { success: false }
      }

      const { onDemandFields, ...regularFields } = jiraIssueTemplateValues

      // Build the Jira issue payload using the same transformation as workflow save
      const template = buildCreateIssuePayload({
        fieldsMetadata,
        fieldsValues: regularFields as {
          [key: string]: string | string[] | undefined
          summary: string
          project: string
          issuetype: string
          reporter: string
          description?: string
          labels?: string[]
        }
      })
      return testTemplate(
        {
          jiraIssue: {
            fields: template.jiraIssueFields.fields,
            onDemandFields
          }
        },
        "jira_issue",
        triggerType || ""
      )
    },
    [triggerType, testTemplate]
  )

  const handleOpenModal = () => {
    setModalType("jira")
  }

  const handleCloseModal = (
    jiraIssueTemplateValues?: any,
    fieldsMetadata?: Record<string, Jira.IssueTypeField>
  ) => {
    if (jiraIssueTemplateValues) {
      const { onDemandFields, ...regularFields } = jiraIssueTemplateValues
      form.setFieldValue([...fieldPath, "fields"], regularFields)
      if (onDemandFields) {
        form.setFieldValue([...fieldPath, "onDemandFields"], onDemandFields)
      }
      // Clear validation errors after successful save
      form.setFieldValue(["actions", path, "relatedError"], undefined)
      // Trigger form validation to mark form as changed
      form.validateFields([fieldPath])
    }
    if (fieldsMetadata) {
      form.setFieldValue(fieldsMetadataPath, fieldsMetadata)
    }

    setModalType(undefined)
  }

  return (
    <div className={styles.actionExtraFields}>
      {!jiraIssueTemplate && (
        <InformationAlert
          type="warning"
          message={t("settings.automatedActions.form.alerts.jiraIssueRequired")}
        />
      )}
      {jiraIssueTemplate && relatedError && (
        <InformationAlert
          type="error"
          message={t(
            "settings.automatedActions.form.alerts.jiraIssueConfigurationError"
          )}
        />
      )}
      <Form.Item name={[path, jiraFieldsMetadataFieldName]} hidden />

      <Form.Item
        name={[path, "jiraIssue"]}
        rules={[
          {
            required: true,
            message: t("error.requiredField")
          }
        ]}
      >
        <CreateTicketButton
          hasIntegration={!!hasIntegration}
          t={getTemplateButtonT({
            t,
            buttonKey: "jiraIssueModal.createButton",
            isNew:
              (id === VALUES_PARAMS.NEW_ENTITY && !isTestSuccess) ||
              !(actions || []).some(
                (action: { type: string; jiraIssue: unknown }) =>
                  action.type === "jira_issue" && !!action.jiraIssue
              ),
            createLabel: t(`${BUTTON_PREFIX}.createJiraIssue`),
            editLabel: t(`${BUTTON_PREFIX}.editJiraIssue`)
          })}
          onClick={handleOpenModal}
          outlined={true}
          typeBtn="secondary"
          sizeBtn="medium"
          disabled={isLoading}
          className={styles.jiraButton}
          below={
            <div className={styles.jiraNote}>
              {t(
                "settings.automatedActions.form.fields.selectActionPlaceholder.jiraTicketNote"
              )}
            </div>
          }
        />
      </Form.Item>
      <JiraTemplateModal
        key={modalType ? "open" : "closed"}
        type={modalType}
        existingTemplate={transformedTemplate}
        closeModal={handleCloseModal}
        validationErrors={relatedError || undefined}
        extra={
          !Array.isArray(extra) && extra?.type === "jira_issue"
            ? extra
            : undefined
        }
        testConfig={{ onTest: handleTest, isLoading: isTestLoading }}
        hasWritePermissions={userHasWritePermission}
      />

      <LinkButton
        icon={<ReadOutlined />}
        size="small"
        type="link"
        className={styles.instructionsButton}
        href={`${EXTERNAL_ROUTES.API_DOCS}/governance/workflows-overview/creating-jira-tickets`}
        target="_blank"
      >
        {t("settings.automatedActions.form.documentation.jiraIssue")}
      </LinkButton>
    </div>
  )
}
